import { Company } from "src/api/client-api/company/useGetOneOrganization.ts";
import {
    SupplierFormContributor,
    SupplierFormIngredientSupplier,
    SupplierFormType,
} from "./SupplierForm/SupplierForm.types";
import { CompanyWrite } from "src/api/client-api/company/useCreateOrganization";
import { formatStringForBackend } from "src/utils/formatting";
import { CompanyGrid } from "src/api/client-api/company/useGetAllOrganization";
import type {
    IngredientSupplierTransportChangeReason,
    IngredientSupplierTransportSelection,
} from "src/components/Transport/ingredientSupplierTransportTypes";
import { getNextIngredientSupplierTransportStatus } from "src/components/Transport/ingredientSupplierTransportPersistence";

export class SupplierService {
    static updateTransportSelections = (
        rows: SupplierFormIngredientSupplier[],
        ingredientIri: IRI,
        transportSelections: IngredientSupplierTransportSelection,
        reason: IngredientSupplierTransportChangeReason,
    ): SupplierFormIngredientSupplier[] =>
        rows.map((row) =>
            row.ingredient["@id"] === ingredientIri ?
                {
                    ...row,
                    transportSelections,
                    transportSelectionsStatus: getNextIngredientSupplierTransportStatus(
                        row.transportSelectionsStatus,
                        reason,
                    ),
                }
            :   row,
        );

    static getSupplierToCreate = (existingSupplier?: Company): SupplierFormType => {
        if (existingSupplier === undefined) {
            return {
                name: null,
                clientId: null,
                users: [],
                ingredientSuppliers: [],

                isProvider: true,
            };
        }

        // Returns a supplier to create from an existing new one.
        const users = existingSupplier.users?.map((user) => {
            const userWrite: SupplierFormContributor = {
                "@id": user["@id"],
                firstname: user.firstname,
                lastname: user.lastname,
            };
            return userWrite;
        });

        const ingSuppliers = existingSupplier.ingredientSuppliers?.map((existingIngSup) => {
            const existingToCreate: SupplierFormIngredientSupplier = {
                "@id": existingIngSup["@id"],
                managedBy:
                    typeof existingIngSup.managedBy === "string" ?
                        existingIngSup.managedBy
                    :   existingIngSup.managedBy?.["@id"],
                ingredient: {
                    "@id": existingIngSup.ingredient["@id"],
                    name: existingIngSup.ingredient.name,
                    ingredientReferenceId: existingIngSup.ingredient.ingredientReferenceId,
                    referenceSource: existingIngSup.ingredient.referenceSource,
                    ingredientLine: existingIngSup.ingredient.ingredientLine,
                },
                quantity: null,
                unit: null,
                transportSelections: {},
                transportSelectionsStatus: "unloaded",
            };
            return existingToCreate;
        });

        return {
            id: existingSupplier.id,
            name: existingSupplier.name,
            clientId: existingSupplier.clientId ?? null,
            users: users ?? [],
            ingredientSuppliers: ingSuppliers ?? [],
            isProvider: existingSupplier.isProvider ?? true,
        };
    };

    static supplierFormToCompanyWrite = (supplier: SupplierFormType): CompanyWrite => {
        const users = supplier.users?.flatMap((user) => {
            if (user["@id"] === undefined) return [];

            return [user["@id"]];
        });

        const supplierToCreate: CompanyWrite = {
            id: supplier.id,
            name: formatStringForBackend(supplier.name),
            clientId: formatStringForBackend(supplier.clientId),
            isProvider: supplier.isProvider,

            users: users,
        };

        return supplierToCreate;
    };

    static makeCompanyListWithSectors = (suppliers: CompanyGrid[]): CompanyGrid[] => {
        return suppliers.map((supplier) => {
            const sectorSet = new Set<string>([]);
            const sectors = supplier.ingredientSuppliers?.reduce((acc, ingredientSupplier) => {
                const sector = ingredientSupplier.ingredient.ingredientLine?.name;
                if (sector === undefined) return acc;

                acc.add(sector);
                return acc;
            }, sectorSet);

            return {
                ...supplier,
                sectors: Array.from(sectors ?? []),
            };
        });
    };
}
